import { notUndefined, useVirtualizer } from '@tanstack/react-virtual' // https://github.com/TanStack/virtual/issues/585#issuecomment-1716173260
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useGlobalFilter, usePagination, useSortBy, useTable, useFilters } from 'react-table'
import '../../../globals/scss/components/tableview-virtualized.scss'
import FormGroup from '../../../legacy/components/FormGroup'
import { RenderIf } from '../../../utils/fns'
import { outputActions, selectOutputState } from '../store/slices/output.slice'
import { ColumnFilter } from './tableHeaders'
import { Button, SvgIcon } from '../../../components'
import { CSVLink } from 'react-csv'
import lowerCase from 'lodash/lowerCase'
import isEqual from 'lodash/isEqual'

export const ShowOptimizedModelsToggler = ({ checked, disabled, onToggle }) => {
  return (
    <FormGroup
      className="tableToggleSwitch"
      labelText="Show Optimized Models"
      popoverText="The Optimized Models show the designer the most cost effective solution(s) required to resist the design tension."
    >
      <input
        type="checkbox"
        checked={checked}
        className="toggleSwitchInput"
        onChange={(e) => onToggle(!e.target.checked)}
        disabled={disabled}
      />
    </FormGroup>
  )
}

ShowOptimizedModelsToggler.propTypes = {
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
}

export const SearchInput = ({ value, onChange }) => {
  return (
    <div className="tableSearch">
      <input
        type="search"
        placeholder="Search Table"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
      <img
        data-src="assets/images/icons/sst-icon-search.svg"
        className="iconic iconic-sm injectSVG"
        alt="search icon"
      />
    </div>
  )
}

SearchInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
}

const VirtualizedWrapper = ({ children, rows, getScrollElement, colSpan }) => {
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement,
    estimateSize: useCallback(() => 34, []),
    overscan: 20,
  })
  const items = virtualizer.getVirtualItems()
  const [before, after] =
    items.length > 0
      ? [
          notUndefined(items[0]).start - virtualizer.options.scrollMargin,
          virtualizer.getTotalSize() - notUndefined(items[items.length - 1]).end,
        ]
      : [0, 0]

  return (
    <>
      {before > 0 && (
        <tr>
          <td colSpan={colSpan} style={{ height: before }} />
        </tr>
      )}
      {children(items)}
      {after > 0 && (
        <tr>
          <td colSpan={colSpan} style={{ height: after }} />
        </tr>
      )}
    </>
  )
}

VirtualizedWrapper.propTypes = {
  children: PropTypes.func.isRequired,
  colSpan: PropTypes.any.isRequired,
  getScrollElement: PropTypes.any.isRequired,
  rows: PropTypes.arrayOf(PropTypes.any).isRequired,
}

const DownloadCSVButton = ({ dataCSV }) => {
  const fileNameCSV = `pfd-output-${new Date().toJSON().slice(0, 10).replace(/-/g, '')}.csv`

  return (
    <CSVLink className="button -tertiary tableCSVDownload" data={dataCSV} filename={fileNameCSV}>
      <img
        data-src="assets/images/icons/sst-icon-download.svg"
        className="iconic iconic-md injectSVG"
        alt="reload"
      />
      <span className="button__text">
        <img data-src="assets/images/icons/sst-font-icon-chevronHeavy.svg" />
        Download CSV
      </span>
    </CSVLink>
  )
}

DownloadCSVButton.propTypes = {
  dataCSV: PropTypes.arrayOf(PropTypes.any).isRequired,
}

const matchesFilter = (value, filterValue) => {
  const normalizedValue = lowerCase(value)
  const selectValue = filterValue?.select ? lowerCase(filterValue.select) : null
  const textValue = filterValue?.text ? lowerCase(filterValue.text) : null

  if (selectValue !== null) return normalizedValue === selectValue
  else if (textValue !== null) return normalizedValue.includes(textValue)
  return false
}

const customTextSelectFilter = (rows, id, filterValue) => {
  if (!filterValue) return rows
  return rows.filter((row) => matchesFilter(row.values[id], filterValue))
}

export const TableViewVirtualized = (props) => {
  const { title, columns = [], serverData = [], disableSearch, sortBy, disableDownloadCSV } = props
  const dispatch = useDispatch()
  const { searchValue } = useSelector(selectOutputState)

  const defaultColumn = useMemo(
    () => ({
      Filter: ColumnFilter,
      filter: customTextSelectFilter,
    }),
    [],
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
    state,
    setAllFilters,
    setSortBy,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data: serverData,
      initialState: {
        sortBy,
        globalFilter: searchValue,
      },
      disableMultiSort: true,
      defaultColumn,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
  )

  const hasFiltering = useMemo(
    () => state.globalFilter || state.filters.length || !isEqual(sortBy, state.sortBy),
    [state.globalFilter, state.filters, state.sortBy, sortBy],
  )

  const resetFilters = () => {
    dispatch(outputActions.updateInput({ searchValue: '' }))

    // clear column filters
    setAllFilters([])
    setGlobalFilter()

    setSortBy(sortBy)
  }

  useEffect(() => {
    dispatch(outputActions.updateInput({ sortBy: state.sortBy }))
  }, [state.sortBy])

  useEffect(() => {
    dispatch(outputActions.updateInput({ searchValue: state.globalFilter }))
  }, [state.globalFilter])

  const virtualizedData = useMemo(() => rows.map((r) => r.original), [rows])

  useEffect(() => {
    dispatch(outputActions.updateInput({ virtualizedData }))
  }, [virtualizedData])

  const scrollElementRef = useRef(null)

  function getDataCSV() {
    const dataReturnCSV = []

    headerGroups?.forEach((headerGroup) => {
      if (!headerGroup.headers?.length) return

      const headersCSV = []
      headerGroup.headers.forEach((header) => headersCSV.push(header.Header))
      dataReturnCSV.push(headersCSV)
    })

    rows?.forEach((row) => {
      if (!row.cells?.length) return

      const cellsCSV = []
      prepareRow(row)
      row.cells.forEach((cell) => cellsCSV.push(cell.value))
      dataReturnCSV.push(cellsCSV)
    })

    return dataReturnCSV
  }

  return (
    <section>
      <h3 className="tableTitle">
        {title}
        {RenderIf(!disableDownloadCSV, <DownloadCSVButton dataCSV={getDataCSV()} />)}
      </h3>

      {RenderIf(
        !disableSearch,
        <div className="tableInfoBar">
          <SearchInput value={state.globalFilter} onChange={setGlobalFilter} />

          <div className="tableInfoToolbar">
            {/* {RenderIf(
              !disableToggleSwitch,
              <ShowOptimizedModelsToggler
                checked={!showAllModels}
                disabled={!canNextPage && page.length < OPTIMIZED_MODELS_LENGTH}
                onToggle={(showAllModels) => {
                  dispatch(outputActions.updateInput({ showAllModels }))
                  setPageSize(getPageSize(showAllModels))
                }}
              />,
            )} */}

            {hasFiltering && (
              <Button variant="secondary" onClick={resetFilters} className="btn-reset-filters">
                <SvgIcon src="assets/images/iconic/filter-clear.svg" />
                Reset filters
              </Button>
            )}
          </div>
        </div>,
      )}

      <div ref={scrollElementRef} className="pfd__scrollable">
        <table {...getTableProps()} className="table -virtualized">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()} className="tr">
                {headerGroup.headers.map((column) => {
                  return (
                    <th
                      key={column.id}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      onClick={(e) => {
                        if (!e.target.classList.contains('icon')) {
                          column.getHeaderProps(column.getSortByToggleProps()).onClick(e)
                        }
                      }}
                      className={
                        column.isSorted
                          ? column.isSortedDesc
                            ? 'th -sortDescending'
                            : 'th -sortAccending'
                          : 'th'
                      }
                    >
                      <div className="headerContent">
                        {column.render('Header')}
                        {column.canFilter && column.Filter && column.render('Filter')}
                      </div>
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            <VirtualizedWrapper
              rows={rows}
              getScrollElement={() => scrollElementRef.current}
              colSpan={visibleColumns.length}
            >
              {(items) =>
                items.map((virtualRow) => {
                  const row = rows[virtualRow.index]
                  prepareRow(row)
                  return (
                    <tr
                      key={row.id}
                      {...row.getRowProps({
                        style: { height: `${virtualRow.size}px` },
                      })}
                      className="tr"
                    >
                      {row.cells.map((cell, index) => {
                        return (
                          <td {...cell.getCellProps()} className="td" key={index}>
                            {cell.render('Cell')}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })
              }
            </VirtualizedWrapper>
          </tbody>
        </table>
      </div>

      {RenderIf(
        !disableSearch,
        <>
          <div className="tableInfoBar">
            <div className="tableResults">
              {RenderIf(
                rows.length > 0,
                `Showing ${rows.length} ${rows.length > 1 ? 'entries' : 'entry'}`,
              )}
            </div>
          </div>
          {RenderIf(
            rows.length === 0,
            <div className="notice -warning">
              <img
                data-src="assets/images/icons/sst-icon-info.svg"
                className="notice__icon iconic iconic-md injectSVG"
                alt="info"
              />
              <div className="notice__text">
                <div className="notice__header">No Matching Records Found</div>
                <div className="notice__body">Try editing or deleting your search criteria.</div>
              </div>
            </div>,
          )}
        </>,
      )}
    </section>
  )
}

TableViewVirtualized.propTypes = {
  title: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  serverData: PropTypes.arrayOf(PropTypes.any).isRequired,
  sortBy: PropTypes.arrayOf(PropTypes.any),
  disableSorting: PropTypes.bool,
  disableSearch: PropTypes.bool,
  disableToggleSwitch: PropTypes.bool,
  disableDownloadCSV: PropTypes.bool,
}
